import React, { useState, useContext, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  FormHelperText,
  Tooltip,
  tooltipClasses,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { IntlContext } from "src/App";
import { InputTextField } from "../UI/textfield";
import { createStyles, makeStyles, styled } from "@mui/styles";
import { useSelector } from "react-redux";
import { Formik } from "formik";
import * as Yup from "yup";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { fetchFromStorage, saveToStorage } from "src/helpers/context/Storage";
import axiosInstance from "src/helpers/context/axiosInstance";
import { URLS } from "src/helpers/constants/url";
import PaymentProcessingModal from "./PaymentProcessingModal";
import MessageBox from "../UI/MessageBox";
import qs from "querystring";
import { Config } from "src/helpers/context/config";
import { ReactComponent as StripeIcon } from "src/assets/images/icons/Stripe.svg";
import { ReactComponent as InfoIcon } from "src/assets/images/icons/infoIcon.svg";
import { PatternFormat } from "react-number-format";
import axios from "axios";
import { identifiers } from "src/helpers/constants/identifier";
import {
  addBreadcrumb,
  captureException,
  trackActivity,
} from "src/helpers/context/sentry";

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      overflow: "auto",
      "& .field_block": {
        "& .MuiButton-root": {
          "&.Mui-disabled": {
            color: "#fff",
            cursor: "not-allowed",
            pointerEvents: "auto",
          },
        },
      },
      "& .MuiFormHelperText-root": {
        fontSize: "16px",
        fontWeight: "500",
        lineHeight: "19px",
        color: "#101010",
        letterSpacing: "0px",
        fontFamily: "'Montserrat', sans-serif",
        margin: "0px",
        paddingBottom: "12px",
        position: "relative",
        "@media (max-width:1199px)": {
          fontSize: "18px",
          lineHeight: "22px",
        },
        "@media (max-width:899px)": {
          fontSize: "16px",
          lineHeight: "20px",
        },
        "@media (max-width:599px)": {
          fontSize: "14px",
          lineHeight: "18px",
        },
        "&.Mui-error": {
          color: "#C01515",
          margin: "6px 0",
          fontSize: "14px",
          lineHeight: "1",
          textTransform: "capitalize",
          padding: "0px",
        },
      },
      "& .date_field": {
        "& input::-webkit-input-placeholder": {
          textTransform: "uppercase",
        },
        "& .MuiInputAdornment-root": {
          display: "none",
        },
      },
    },
  })
);

const PaymentSection = ({
  cart,
  cartTotalAmount,
  cartItem,
  cartTotal,
  address,
}) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const intlContext = useContext(IntlContext);
  const gothe_mile = intlContext?.messages;
  const redux_auth = useSelector((state) => state?.ActionReducer?.authdata);
  const auth = redux_auth ? redux_auth : fetchFromStorage("authdata");
  const [processingStep, setProcessingStep] = useState(null);
  const [paymentRefId, setPaymentRefId] = useState(null);
  const invoiceParamsRef = useRef(null);
  const [paymentSuccess, setpaymentSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [invalidUserMessage, setInvalidUserMessage] = useState(false);
  const current_location = fetchFromStorage(identifiers?.currentLocation);

  const GSTAmount = ((cartTotal * cart?.gst?.split("%")[0]) / 100)?.toFixed(2);
  const orderTotal = (parseFloat(cartTotal) + parseFloat(GSTAmount))?.toFixed(
    2
  );
  const payableAmount =
    Object.keys(cartTotalAmount)?.length > 0
      ? cartTotalAmount?.discount?.toFixed(2)
      : orderTotal;

  const stripeAuthHeader = {
    "Content-Type": "application/x-www-form-urlencoded",
    Authorization: `Bearer ${Config?.StripeApikeySK}`,
  };

  const getCountry = async () => {
    try {
      const { data, status } = await axios.get("https://ipapi.co/json/");
      if (status === 200) {
        saveToStorage(identifiers?.currentLocation, data);
      }
    } catch (error) {
      captureException(error, {
        source: "geo.ipapi",
        tags: { feature: "payment" },
      });
      // country fetch failed, default 'AU' will be used
    }
  };

  useEffect(() => {
    if (!current_location?.country) {
      getCountry();
    }
  }, [current_location]);

  useEffect(() => {
    trackActivity("checkout.started", {
      cartId: cart?._id,
      itemCount: cartItem?.length,
      amount: payableAmount,
      userId: auth?.data?._id,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const invoiceUrlMail = async (order_id, resetForm) => {
    setProcessingStep("invoicing");
    addBreadcrumb({
      category: "payment",
      message: "invoice.generate.start",
      level: "info",
      data: { order_id },
    });
    let payload = {
      order: order_id,
      gst_state: current_location?.country ? current_location?.country : "AU",
    };
    try {
      const { data, status } = await axiosInstance.post(
        URLS?.orderInvoiceViewUrl,
        payload
      );
      if (status === 200 && data?.value === true) {
        let payload = {
          invoiceUrl: data?.data?.invoiceUrl,
          order: order_id,
        };
        try {
          const { data, status } = await axiosInstance.post(
            URLS?.orderInvoiceMail,
            payload
          );
          if (status === 200 && data?.value === true) {
            trackActivity("payment.invoice_sent", { order_id });
            setProcessingStep(null);
            navigate("/thankyou");
            resetForm();
          } else {
            trackActivity("payment.invoice_mail_failed", { order_id });
            setProcessingStep("invoice_failed");
          }
        } catch (error) {
          captureException(error, {
            source: "payment.invoiceMail",
            order_id,
          });
          setProcessingStep("invoice_failed");
        }
      } else {
        trackActivity("payment.invoice_url_failed", { order_id });
        setProcessingStep("invoice_failed");
      }
    } catch (error) {
      captureException(error, {
        source: "payment.invoiceViewUrl",
        order_id,
      });
      setProcessingStep("invoice_failed");
    }
  };

  const HtmlTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
      color: "#F2F2F2",
      "&::before": {
        border: "1px solid #B0B0B0",
        borderRadius: "5px",
      },
    },
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: "#F2F2F2",
      // color: '#F2F2F2',
      maxWidth: 470,
      fontSize: "25px",
      border: "1px solid #B0B0B0",
      borderRadius: "5px",
      padding: "24px",
      "@media (max-width:1199px)": {
        maxWidth: 380,
        padding: "23px",
      },
      "@media (max-width:899px)": {
        maxWidth: 320,
        fontSize: "22px",
        padding: "18px",
      },
      "@media (max-width:599px)": {
        maxWidth: 230,
        fontSize: "20px",
        padding: "16px",
      },
    },
  }));

  const metaPayload = (data) => {
    const productData = [];

    data?.forEach((item) => {
      // Add categoryLevel2 (Omnibus) data if available
      if (item?.categoryLevel2) {
        productData.push({
          productId: item?.categoryLevel2?._id,
          productName: item?.categoryLevel2?.name,
          type: "Omnibus",
          // productName: item?.packageName?.join("-"), // for join all the name same as cart list name
        });
      }

      // Add categoryLevel3 (Chapter) data if available
      if (item?.categoryLevel3) {
        productData.push({
          productId: item?.categoryLevel3?._id,
          productName: item?.categoryLevel3?.name,
          type: "Chapter",
          // productName: item?.packageName?.join("-"), // for join all the name same as cart list name
        });
      }

      // Add productForm (Test) data if available
      if (item?.productForm) {
        productData.push({
          productId: item?.productForm?._id,
          productName: item?.productForm?.name,
          type: "Test",
          // productName: `${item?.packageName?.join(" - ")} - ${
          //   item.productForm.name
          // }`, // for join all the name same as cart list name
        });
      }
    });

    return {
      email: cart?.user?.email,
      user: cart?.user?._id,
      discountAmount: cartTotalAmount?.perDiscount?.toFixed(2) || "",
      couponCode: cartTotalAmount?.coupon?.code || "",
      productData: JSON.stringify(
        Array.isArray(productData) && productData?.length > 0 ? productData : []
      ),
    };
  };

  return (
    <>
      <PaymentProcessingModal
        step={processingStep}
        paymentRefId={paymentRefId}
        onRetry={() => {
          if (invoiceParamsRef.current) {
            const { order_id, resetForm } = invoiceParamsRef.current;
            invoiceUrlMail(order_id, resetForm);
          }
        }}
      />
      <MessageBox
        open={paymentSuccess}
        handleClose={() => setpaymentSuccess(!paymentSuccess)}
        content={<Typography>{gothe_mile?.message?.paymentSuccess}</Typography>}
      />
      <MessageBox
        open={invalidUserMessage}
        handleClose={() => setInvalidUserMessage(!invalidUserMessage)}
        content={
          <Typography className="text-danger">{errorMessage}</Typography>
        }
      />

      {/* BILLING ADDRESS */}

      <Box className="field_block">
        <Formik
          enableReinitialize={true}
          initialValues={{
            card_number: "",
            securitycode: "",
            cardExpiryDate: "",
          }}
          validationSchema={
            payableAmount > 0 &&
            Yup.object().shape({
              card_number: Yup.string().required(
                gothe_mile?.validation?.required_message
              ),
              cardExpiryDate: Yup.string()
                .trim()
                .required(gothe_mile?.validation?.required_message),
              securitycode: Yup.string().required(
                gothe_mile?.validation?.required_message
              ),
            })
          }
          onSubmit={async (value, { resetForm }) => {
            if (payableAmount && payableAmount == "0.00") {
              let payload = {
                amount: payableAmount,
                email: auth?.data?.email,
                metaData: metaPayload(cartItem),
                order_save: {
                  address: {
                    city: address?.address?.city,
                    contryCode: address?.address?.contryCode,
                    country: address?.address?.country,
                    line1: address?.address?.line1,
                    line2: address?.address?.line2,
                    postal_code: address?.address?.postal_code,
                    state: address?.address?.state,
                  },
                  cart: cart?._id,
                  contact: address?.contact,
                  contactNumber: address?.contact,
                  coupon: cartTotalAmount?.coupon?._id,
                  email: auth?.data?.email,
                  name: auth?.data?.name,
                  isActive: true,
                  orderDate: moment(new Date()).format("YYYY-MM-DD"),
                  productForm: cartItem,
                  status: address?.status,
                  subTotal: payableAmount,
                  gst_amount: GSTAmount && GSTAmount,
                  user: auth?.data?._id,
                },
              };
              setProcessingStep("processing");
              trackActivity("payment.free_order.start", {
                amount: payableAmount,
                userId: auth?.data?._id,
                email: auth?.data?.email,
                cartId: cart?._id,
                itemCount: cartItem?.length,
                gstAmount: GSTAmount,
                country: current_location?.country || "AU",
                coupon: cartTotalAmount?.coupon?.code || null,
              });
              try {
                const { data, status } = await axiosInstance.post(
                  URLS?.withoutPaymentOrder,
                  payload
                );
                if (status === 200 && data?.value === true) {
                  let order_id = data?.data?.order_id;
                  trackActivity("payment.free_order.success", {
                    order_id,
                    amount: payableAmount,
                    cartId: cart?._id,
                    user_id: auth?.data?._id,
                    user_email: auth?.data?.email,
                    user_name: auth?.data?.name,
                    itemCount: cartItem?.length,
                  });
                  setPaymentRefId(order_id);
                  invoiceParamsRef.current = { order_id, resetForm };
                  invoiceUrlMail(order_id, resetForm);
                } else {
                  trackActivity(
                    "payment.free_order.failed",
                    {
                      message: data?.error?.message || data?.message,
                      value: data?.value,
                      cartId: cart?._id,
                    },
                    { level: "warning" }
                  );
                  setProcessingStep(null);
                }
              } catch (error) {
                captureException(error, {
                  source: "payment.free_order",
                  tags: { feature: "payment" },
                  amount: payableAmount,
                  cartId: cart?._id,
                  userId: auth?.data?._id,
                });
                setProcessingStep(null);
              }
            } else {
              let payload = qs.stringify({
                "card[number]": value?.card_number,
                // 'card[exp_month]': parseInt(moment(value?.cardExpiryDate).format("MM")),
                // 'card[exp_year]': parseInt(moment(value?.cardExpiryDate).format("YYYY")),
                // 'card[exp_month]': parseInt(
                //   moment(value?.cardExpiryDate?.split('/')[0]).format('MM')
                // ),
                "card[exp_month]": parseInt(
                  moment(value?.cardExpiryDate, "MM/YYYY").format("MM")
                ),
                "card[exp_year]": parseInt(
                  moment(
                    new Date().getFullYear().toString().slice(0, 2) +
                      value?.cardExpiryDate?.split("/")[1]
                  ).format("YYYY")
                ),
                "card[cvc]": value?.securitycode,
              });
              setProcessingStep("processing");
              trackActivity("payment.stripe.start", {
                amount: payableAmount,
                userId: auth?.data?._id,
                email: auth?.data?.email,
                cartId: cart?._id,
                itemCount: cartItem?.length,
                gstAmount: GSTAmount,
                country: current_location?.country || "AU",
                coupon: cartTotalAmount?.coupon?.code || null,
                hasExpiry: Boolean(value?.cardExpiryDate),
                cvcLength: value?.securitycode?.length || 0,
                cardLast4: value?.card_number
                  ? String(value.card_number).replace(/\s/g, "").slice(-4)
                  : undefined,
              });
              try {
                const { data, status } = await axiosInstance.post(
                  Config?.StripeApiURL + URLS?.orderTokens,
                  payload,
                  {
                    headers: stripeAuthHeader,
                  }
                );
                if (status === 200) {
                  // if (status === 200 && data?.value === true) {
                  let payload = {
                    amount: payableAmount,
                    email: auth?.data?.email,
                    id: data?.id,
                    order_save: {
                      address: {
                        city: address?.address?.city,
                        contryCode: address?.address?.contryCode,
                        country: address?.address?.country,
                        line1: address?.address?.line1,
                        line2: address?.address?.line2,
                        postal_code: address?.address?.postal_code,
                        state: address?.address?.state,
                      },
                      cart: cart?._id,
                      contact: address?.contact,
                      contactNumber: address?.contact,
                      coupon: cartTotalAmount?.coupon?._id,
                      email: auth?.data?.email,
                      name: auth?.data?.name,
                      orderDate: moment(new Date()).format("YYYY-MM-DD"),
                      productForm: cartItem,
                      status: address?.status,
                      subTotal: payableAmount,
                      gst_amount: GSTAmount && GSTAmount,
                      user: auth?.data?._id,
                    },
                    metaData: metaPayload(cartItem),
                  };
                  addBreadcrumb({
                    category: "payment",
                    message: "stripe.token.created",
                    level: "info",
                    data: {
                      tokenId: data?.id,
                      brand: data?.card?.brand,
                      last4: data?.card?.last4,
                      exp_month: data?.card?.exp_month,
                      exp_year: data?.card?.exp_year,
                      funding: data?.card?.funding,
                      country: data?.card?.country,
                    },
                  });
                  try {
                    const { data, status } = await axiosInstance.post(
                      URLS?.orderPayWithStripe,
                      payload
                    );
                    if (status === 200 && data?.value === true) {
                      let order_id = data?.data?.order_id;
                      trackActivity("payment.stripe.success", {
                        order_id,
                        amount: payableAmount,
                        cartId: cart?._id,
                        user_id: auth?.data?._id,
                        user_email: auth?.data?.email,
                        user_name: auth?.data?.name,
                        itemCount: cartItem?.length,
                        gst_amount: GSTAmount,
                        coupon: cartTotalAmount?.coupon?.code || null,
                      });
                      setPaymentRefId(order_id);
                      invoiceParamsRef.current = { order_id, resetForm };
                      invoiceUrlMail(order_id, resetForm);
                    } else {
                      trackActivity(
                        "payment.stripe.failed",
                        {
                          message: data?.error?.message,
                          cartId: cart?._id,
                          amount: payableAmount,
                        },
                        { level: "warning" }
                      );
                      setProcessingStep(null);
                      setInvalidUserMessage(true);
                      setErrorMessage(data?.error?.message);
                    }
                  } catch (error) {
                    captureException(error, {
                      source: "payment.stripe.charge",
                      tags: { feature: "payment" },
                      amount: payableAmount,
                      cartId: cart?._id,
                      userId: auth?.data?._id,
                    });
                    setProcessingStep(null);
                    setInvalidUserMessage(true);
                    setErrorMessage(gothe_mile?.message?.somethingWrongMsg);
                  }
                } else {
                  trackActivity(
                    "payment.stripe.token_failed",
                    { amount: payableAmount, cartId: cart?._id },
                    { level: "warning" }
                  );
                  setProcessingStep(null);
                }
              } catch (error) {
                captureException(error, {
                  source: "payment.stripe.token",
                  tags: { feature: "payment" },
                  amount: payableAmount,
                  cartId: cart?._id,
                  stripeError: error?.response?.data?.error?.type,
                  stripeCode: error?.response?.data?.error?.code,
                  stripeMessage: error?.response?.data?.error?.message,
                });
                setProcessingStep(null);
                setInvalidUserMessage(true);
                setErrorMessage(error?.response?.data?.error?.message);
              }
            }
          }}
        >
          {({
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            values,
            setFieldValue,
          }) => (
            <form onSubmit={handleSubmit}>
              {/* PAYMENT SECTION */}
              <Box className={classes.root}>
                <Box className="arrow"></Box>

                <Box className="field_block">
                  {payableAmount && payableAmount == "0.00" ? (
                    <Box sx={{ textAlign: "center" }}>
                      <Button
                        type="submit"
                        variant="subscribe_btn"
                        disabled={processingStep !== null}
                        sx={{
                          mt: { xs: "28px", md: "38px", lg: "50px" },
                        }}
                      >
                        {gothe_mile?.subscribe}
                      </Button>
                    </Box>
                  ) : (
                    <Grid container spacing={2.25}>
                      <Grid item size={{ xs: 12 }}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <FormHelperText>
                            {gothe_mile?.card_number}
                          </FormHelperText>
                          <HtmlTooltip
                            title={
                              <Typography
                                sx={{ fontSize: "14px", lineHeight: "19.5px" }}
                              >
                                {gothe_mile?.toolTip_content}
                              </Typography>
                            }
                            placement="right"
                            arrow
                          >
                            <Button
                              sx={{
                                minWidth: "12.5px",
                                p: "0",
                                m: "0 0 12px 8px",
                              }}
                            >
                              <InfoIcon />
                            </Button>
                          </HtmlTooltip>
                        </Box>
                        <InputTextField
                          inputProps={{ maxLength: 19 }}
                          fullWidth
                          className="form_field"
                          placeholder={gothe_mile?.card_number}
                          type="text"
                          name="card_number"
                          value={values?.card_number
                            .replace(/[^\dA-Z]/g, "")
                            .replace(/(.{4})/g, "$1 ")
                            .trim()}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(
                            touched?.card_number && errors?.card_number
                          )}
                          helperText={
                            touched?.card_number && errors?.card_number
                          }
                          onKeyPress={(e) => {
                            if (!/[0-9]/.test(e.key)) {
                              e.preventDefault();
                            }
                          }}
                        />
                      </Grid>
                      {/* <Grid item size={{ xs: 12 }}> */}
                      {/* <Grid container> */}
                      <Grid item size={{ xs: 6 }}>
                        <FormHelperText>
                          {gothe_mile?.expiry_date}
                        </FormHelperText>
                        <PatternFormat
                          fullWidth
                          className="form_field date_field"
                          name="cardExpiryDate"
                          value={values?.cardExpiryDate}
                          format="##/##"
                          customInput={InputTextField}
                          placeholder="MM/YY"
                          onChange={handleChange}
                          error={Boolean(
                            touched?.cardExpiryDate && errors?.cardExpiryDate
                          )}
                          helperText={
                            touched?.cardExpiryDate && errors?.cardExpiryDate
                          }
                        />
                      </Grid>
                      <Grid item size={{ xs: 6 }}>
                        <FormHelperText>{gothe_mile?.cvc_code}</FormHelperText>
                        <InputTextField
                          slotProps={{ minLength: 3, maxLength: 4 }}
                          fullWidth
                          className="form_field"
                          placeholder={gothe_mile?.security_code}
                          type="text"
                          name="securitycode"
                          error={Boolean(
                            touched?.securitycode && errors?.securitycode
                          )}
                          helperText={
                            touched?.securitycode && errors?.securitycode
                          }
                          onChange={handleChange}
                          value={values?.securitycode}
                          onBlur={handleBlur}
                          onKeyDown={(e) => {
                            if (!/[0-9]/.test(e.key)) {
                              e.preventDefault();
                            }
                          }}
                        />
                      </Grid>
                      {/* </Grid> */}
                      {/* </Grid> */}
                      <Grid item size={{ xs: 12 }} sx={{ textAlign: "center" }}>
                        <Button
                          type="submit"
                          variant="subscribe_btn"
                          disabled={processingStep !== null}
                          sx={{ mt: { xs: "18px", md: "36px", lg: "50px" } }}
                        >
                          {gothe_mile?.placeOrder}
                        </Button>
                        <Box
                          sx={{
                            py: "27px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderBottom: "1px solid #D4D4D4",
                          }}
                        >
                          <Typography sx={{ lineHeight: "19px" }}>
                            {gothe_mile?.powered_by}
                          </Typography>
                          <StripeIcon style={{ marginLeft: "12px" }} />
                        </Box>
                      </Grid>
                    </Grid>
                  )}
                </Box>
              </Box>
            </form>
          )}
        </Formik>
      </Box>
    </>
  );
};
export default PaymentSection;
